import React, { forwardRef, useState, useEffect, useCallback, useRef } from 'react'
import { DraggableProvided, DraggableStateSnapshot, DraggableProvidedDraggableProps } from 'react-beautiful-dnd'
import { DraggableBox, DragContainer } from './style'
import TextArea from '../TextArea'
import { Size } from '..'
import Icon, { IconName, IconSize } from 'particles/Icon'
export interface TextListProps {
    name: string
    placeholder: string
    width?: string
    onChange: (value: string) => void
    onDelete?: (e: React.MouseEvent<HTMLButtonElement>) => void
    onCommentClick?: (e: React.MouseEvent<HTMLDivElement>, targetElement: HTMLDivElement | null) => void
    isDeletable?: boolean
    error?: string
    isReadOnly?: boolean
    isDraggable: boolean
    className?: string
    value: string
    isDisabled?: boolean
    isCommentable?: boolean
    isCommented?: boolean
    inputIcon?: IconName
    inputIconSize?: IconSize
    prefix?: string
    identifier?: string
    size?: Size
    tooltipContext?: { title?: string; label?: string }
    isActivated?: boolean
    id?: string
    dataTestId?: string
    showError?: boolean
}

const TextList = forwardRef<
    HTMLDivElement | null,
    TextListProps & {
        draggableProvided?: DraggableProvided
        snapshot?: DraggableStateSnapshot
    }
>(
    (
        {
            width,
            placeholder,
            onDelete,
            onChange,
            onCommentClick,
            isDeletable = true,
            error,
            isReadOnly = false,
            draggableProvided,
            snapshot,
            isDraggable = true,
            value,
            size = 'm',
            isDisabled = false,
            isCommentable = false,
            isCommented = false,
            inputIcon,
            inputIconSize,
            prefix,
            tooltipContext = { title: '', label: '' },
            isActivated = false,
            className,
            identifier,
            id,
            dataTestId,
            showError,
            ...props
        },
        ref,
    ) => {
        const commentIcon = useRef<HTMLDivElement | null>(null)
        const [isActive, setIsActive] = useState(isActivated)
        const [displayValue, setDisplayValue] = useState(value)

        const handleCommentClick = useCallback(
            (e: React.MouseEvent<HTMLDivElement>): void => {
                if (isCommentable && onCommentClick) {
                    setIsActive(prev => !prev)
                    onCommentClick(e, commentIcon.current)
                }
            },
            [isCommentable, onCommentClick],
        )
        const handleUpdate = (value: string) => {
            setDisplayValue(value)
            if (onChange) {
                onChange(value)
            }
        }

        useEffect(() => {
            setIsActive(isActivated)
        }, [isActivated])

        // since props may includes DraggableProvidedDraggableProps and cause the textarea be shift with react-beautiful-dnd's dragging transition, needs to excludes it from props
        const propsWithoutDragProps = { ...props }
        const draggableKeys: (keyof DraggableProvidedDraggableProps)[] = [
            'style',
            'data-rbd-draggable-context-id',
            'data-rbd-draggable-id',
            'onTransitionEnd',
        ]
        draggableKeys.forEach(key => {
            delete (propsWithoutDragProps as any)[key]
        })

        return (
            <DragContainer
                width={width}
                className={className}
                isCommentable={isCommentable}
                isCommented={isCommented}
                isActive={isActive}
                ref={ref}
                isDragging={snapshot?.isDragging}
                size={size}
                id={id}
                onClick={(e: any) => {
                    if (e.target.id === id) {
                        handleCommentClick(e)
                    }
                }}
                {...props}
            >
                {draggableProvided && isDraggable && !isReadOnly ? (
                    <DraggableBox {...draggableProvided.dragHandleProps}>
                        <Icon name="dotsSixVertical" size="20px" />
                    </DraggableBox>
                ) : null}
                <TextArea
                    {...propsWithoutDragProps}
                    placeholder={placeholder}
                    onChange={(value: string) => {
                        handleUpdate(value)
                    }}
                    size={size}
                    value={displayValue}
                    minTextAreaHeight={32}
                    tooltipContext={tooltipContext}
                    isDisabled={isDisabled}
                    isReadOnly={isReadOnly}
                    isDeletable={isDeletable && !!onDelete}
                    onDelete={onDelete}
                    inputIcon={inputIcon}
                    inputIconSize={inputIconSize}
                    error={error}
                    showError={showError}
                    className="textarea-container"
                    dataTestId={dataTestId}
                />
                {isCommentable ? (
                    <div ref={commentIcon} onClick={e => handleCommentClick(e)}>
                        <Icon className="comment-icon" name={isCommented ? 'chatText' : 'commentPlus'} size="20px" />
                    </div>
                ) : null}
            </DragContainer>
        )
    },
)

TextList.displayName = 'TextList'

export default TextList
